/**
 * Tiny logging helper. Only emits console output in development so production
 * builds stay quiet, and forwards `error` + `warn` events to Sentry (when the
 * DSN is configured). Prefer this over `console.log`/`console.error` in app
 * code so we can flip logging behaviour or route to a service without touching
 * every call site.
 */
import { captureException, captureMessage } from './sentry';

const isDev =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production';

function safeCall(method, args) {
  if (!isDev) return;
  try {
    (console[method] || console.log).apply(console, args);
  } catch (err) {
    // Deliberate no-op: this catch guards against exotic environments where
    // `console` has been shimmed out. We cannot log the failure (that would
    // recurse), so we swallow it. The variable is referenced below to make the
    // suppression explicit to static analysis.
    void err;
  }
}

/**
 * Extract the Error-like argument from a logger call. We look through all args
 * for the first `Error` instance so callers can pass a context string plus
 * the caught exception in either order.
 */
function findError(args) {
  for (const a of args) {
    if (a instanceof Error) return a;
  }
  return null;
}

function forwardError(args) {
  try {
    const err = findError(args);
    // Concatenate the non-Error args as the breadcrumb message
    const label = args
      .filter((a) => !(a instanceof Error))
      .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
      .join(' ')
      .trim();
    if (err) {
      captureException(err, label ? { context: label } : undefined);
    } else if (label) {
      captureMessage(label, 'error');
    }
  } catch (_) {
    /* never let telemetry break the app */
  }
}

function forwardWarn(args) {
  try {
    const label = args
      .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
      .join(' ')
      .trim();
    if (label) captureMessage(label, 'warning');
  } catch (_) {
    /* swallow */
  }
}

function safeStringify(v) {
  try {
    return JSON.stringify(v);
  } catch (_) {
    return String(v);
  }
}

export const logger = {
  log: (...args) => safeCall('log', args),
  info: (...args) => safeCall('info', args),
  warn: (...args) => {
    safeCall('warn', args);
    forwardWarn(args);
  },
  error: (...args) => {
    safeCall('error', args);
    forwardError(args);
  },
  debug: (...args) => safeCall('debug', args),
};

export default logger;
